import React, { ChangeEvent, Dispatch, MouseEvent, useMemo, useRef, useState } from 'react';
import classnames from 'classnames';

import { fc } from 'i18n';
import { List } from './list';
import { chevronDown } from 'assets/icons';
import { ISelectOption } from 'types/select';
import Input from 'components/molecules/input';
import { getIsSubstring } from 'helpers/getIsSubString';
import { useOnClickOutside } from 'hooks/useOnClickOutside';

import styles from './styles.module.css';

interface ISelectProps<T> {
  isLoading?: boolean;
  disabled?: boolean;
  withSearch?: boolean;
  readOnly?: boolean;
  required?: boolean;
  error?: string;
  mainPlaceholder?: string;
  addPlaceholder?: string;
  selected?: ISelectOption<T> | null;
  options: ISelectOption<T>[] | null;
  onChange?: Dispatch<ISelectOption<T>>;
  onAdd: (name: string) => () => void;
}

export const SelectPickerWithCreate = <T,>(props: ISelectProps<T>) => {
  const {
    isLoading,
    withSearch,
    selected,
    options,
    mainPlaceholder,
    addPlaceholder,
    onChange,
    onAdd,
    readOnly,
    ...rest
  } = props;

  const wrapperRef = useRef(null);

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [filter, setFilter] = useState<string>('');

  const onClose = () => {
    setIsOpen(false);
    setFilter('');
  };

  useOnClickOutside([wrapperRef], onClose);

  const filteredOptions = useMemo(
    () => (!filter ? options : options?.filter(({ label }) => getIsSubstring(label, filter))),
    [options, filter],
  );

  const placeholder = withSearch ? selected?.label || fc(mainPlaceholder || 'select') : fc(mainPlaceholder || 'select');

  const contentClassName = classnames(styles.content, { [styles.notEmpty]: !!selected && withSearch });

  const onFilterChange = (event: ChangeEvent<HTMLInputElement>) => setFilter(event.target.value);

  const toggleDropdown = () => {
    !readOnly && setIsOpen(!isOpen);
  };

  const selectOption = (option: ISelectOption<T>) => (event: MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    !!onChange && onChange(option);
    onClose();
  };

  return (
    <div className={styles.wrapper}>
      <div className={contentClassName} ref={wrapperRef}>
        <Input
          placeholder={placeholder}
          label={fc('tracker name')}
          value={withSearch ? filter : selected?.label || ''}
          icon={chevronDown}
          onChange={onFilterChange}
          onClick={toggleDropdown}
          {...rest}
        />
        {isOpen && (
          <List
            isLoading={isLoading}
            selectedId={selected?.value}
            options={filteredOptions || null}
            placeholder={addPlaceholder}
            selectOption={selectOption}
            add={onAdd}
          />
        )}
      </div>
    </div>
  );
};
